import React, { FC, useState, MouseEvent, createRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  Checkbox,
  Grow,
  Paper,
  Popper,
  TextField,
  ClickAwayListener,
  Button
} from '@mui/material';
import { isEmpty } from 'lodash';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { translate } from '@utils/locale';
import { DropDownIcon } from '@assets/icons';
import { colors } from '@constants/colors';
import { useCreateSectorMutation } from '@utils/api';
import { Sector } from '@type/util';
import { showGenericErrorToast } from '@utils/error';
import { ToastTypes } from '@type/toast';
import displayToast from '@components/toast/customToast';

const SectorAndCategory: FC<{
  isEditMode?: boolean;
  errors?: object;
  sectors: Sector[];
  description?: string;
}> = ({ isEditMode, errors, sectors, description }) => {
  const { getValues, setValue, clearErrors } = useFormContext();

  const [createSector] = useCreateSectorMutation();

  const sectorsList = sectors
    ? sectors.map(item => item.name).filter(item => !!item)
    : [];

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [numOptions, setNumOptions] = useState(0);
  const [sectorAnchorEl, setSectorAnchorEl] = useState<
    (EventTarget & HTMLDivElement) | null
  >(null);
  const paperRef = createRef<HTMLDivElement>();
  const isSelectSectorPopOverOpen = Boolean(sectorAnchorEl);

  const onSelectSectorComponentClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isEditMode)
      setSectorAnchorEl(sectorAnchorEl ? null : event.currentTarget);
  };

  const onSelectSectorComponentClose = () => {
    setSectorAnchorEl(null);
    // handleOnClose?.();
  };

  const onActionButtonClick = async (isCreate: boolean) => {
    if (isCreate) {
      try {
        const res = await createSector({
          sector_name: searchTerm
        }).unwrap();
        if (res.result === 'SUCCESS') {
          setSelectedItems([...selectedItems, searchTerm]);
        } else {
          displayToast(
            ToastTypes.ERROR,
            res.error?.details?.[0] ?? 'Failed to create sector'
          );
        }
      } catch (err) {
        showGenericErrorToast(err);
      }
    } else {
      clearErrors('sector');
      setValue(
        'sector',
        selectedItems.reduce((acc, val) => {
          if (acc) return `${acc}, ${val}`;
          return val;
        }, '')
      );
      onSelectSectorComponentClose();
    }
  };

  useEffect(() => {
    setNumOptions(
      paperRef?.current?.querySelectorAll('li[data-option-index]')?.length ?? 0
    );
  }, [searchTerm]);

  useEffect(() => {
    const values = getValues('sector');
    if (values) setSelectedItems(values.split(', '));
    else setSelectedItems([]);
  }, [isSelectSectorPopOverOpen]);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedItems]);

  const sectorText = getValues('sector')
    ? {
        value: getValues('sector')
          .split(', ')
          .reduce((acc: string, val: string, index: number, arr: string[]) => {
            if (arr.length > 2) {
              if (index < 2) {
                return `${acc ? `${acc}, ` : acc}${val}`;
              }
              if (index === 2) return `${acc} +${arr.length - 2} more`;
              return acc;
            }
            return `${acc ? `${acc}, ` : acc}${val}`;
          }, ''),
        textColor: 'text-black'
      }
    : {
        value: translate('company.brandSection.sector'),
        textColor: 'text-[#000000ab]'
      };

  return (
    <>
      <div
        className="w-full"
        onClick={onSelectSectorComponentClick}
        id={'sectorAndCategory'}>
        <div
          className={`flex justify-between items-center p-3 mt-4 w-full h-14 ${
            isEditMode ? sectorText.textColor : '!text-[#000000ab]'
          } rounded-[5px] border cursor-pointer ${
            !isEmpty(errors) &&
            (
              errors['sector'] as {
                message?: string;
              }
            )?.message
              ? 'border-red'
              : 'border-gray88'
          }`}>
          {sectorText.value}
          <DropDownIcon fill={colors.gray} />
        </div>
        {!isEmpty(errors) &&
        (errors['sector'] as { message?: string })?.message ? (
          <p className="mx-[14px] mt-[3px] text-xs leading-5 text-red">
            {(errors['sector'] as { message?: string })?.message}
          </p>
        ) : (
          description && (
            // eslint-disable-next-line tailwindcss/no-custom-classname
            <p
              className={`mx-[14px] mt-[3px] text-xs leading-5 text-black ${
                isEditMode ? 'text-opacity-60' : 'text-opacity-40'
              }`}>
              {description}
            </p>
          )
        )}
      </div>
      <Popper
        open={isSelectSectorPopOverOpen}
        anchorEl={sectorAnchorEl}
        sx={{
          zIndex: 1
        }}
        role={undefined}
        transition
        disablePortal
        style={{
          width: document.getElementById('sectorAndCategory')?.clientWidth
        }}>
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'center bottom'
            }}>
            <Paper
              sx={{
                borderRadius: 0,
                borderTopRightRadius: '5px',
                borderTopLeftRadius: '5px',
                borderBottomWidth: 0
              }}>
              <ClickAwayListener onClickAway={() => onActionButtonClick(false)}>
                <div className="p-0 w-full">
                  <Autocomplete
                    value={selectedItems}
                    open={isSelectSectorPopOverOpen}
                    multiple
                    limitTags={2}
                    options={sectorsList}
                    disableCloseOnSelect
                    getOptionLabel={option => option}
                    className="mt-1 w-full"
                    disabled={!isEditMode}
                    onChange={(_, values) => setSelectedItems(values)}
                    renderOption={(autoCompleteProps, option, { selected }) => (
                      <li {...autoCompleteProps}>
                        <span className="w-full">{option}</span>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={translate('company.brandSection.search')}
                        variant="standard"
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            padding: '20px',
                            borderBottomWidth: '1px'
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          endAdornment: undefined
                        }}
                        autoFocus
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                    )}
                    PaperComponent={({ children }) => {
                      const isCreate =
                        !!searchTerm &&
                        (!numOptions ||
                          !sectorsList
                            .map(val => val.toLowerCase())
                            .includes(searchTerm.toLowerCase()));
                      return (
                        <Paper
                          ref={paperRef}
                          sx={{
                            borderRadius: 0,
                            borderBottomRightRadius: '5px',
                            borderBottomLeftRadius: '5px',
                            borderTopWidth: 0
                          }}>
                          {children}
                          {isCreate && (
                            <div className="sticky bottom-0 py-1 border-t">
                              <Button
                                variant="text"
                                color="inherit"
                                // eslint-disable-next-line tailwindcss/no-custom-classname
                                className="w-full text-start"
                                onMouseDown={() =>
                                  onActionButtonClick(isCreate)
                                }>
                                <div className="px-2 w-full text-blue-500 break-words">
                                  {translate(
                                    'company.brandSection.createSector',
                                    { sector: searchTerm }
                                  )}
                                </div>
                              </Button>
                            </div>
                          )}
                        </Paper>
                      );
                    }}
                  />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SectorAndCategory;
